import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { format, parseISO, isValid, getHours, isSameDay } from 'date-fns';
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Timezone, convertTimezone, formatDateInTimezone } from '../../utils/timezoneUtils';

interface HourlyTicketChartProps {
  incidents: any[];
  catalogTasks: any[];
  selectedDate: Date;
  timezone?: Timezone;
}

export function HourlyTicketChart({ incidents, catalogTasks, selectedDate, timezone }: HourlyTicketChartProps) {
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const { hourlyData, ticketsByHour } = useMemo(() => {
    // Initialize hourly buckets (0-23 hours)
    const hourlyBuckets = Array.from({ length: 24 }, (_, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      hourNumber: hour,
      incidents: 0,
      catalogTasks: 0,
      total: 0
    }));

    // Initialize tickets by hour tracking
    const ticketsTracker: Record<string, Array<{
      number: string;
      description: string;
      openedTime: string;
      type: 'incident' | 'task';
      priority?: string;
      sortTime: Date;
    }>> = {};

    // Initialize all hours
    for (let i = 0; i < 24; i++) {
      const hourKey = `${i.toString().padStart(2, '0')}:00`;
      ticketsTracker[hourKey] = [];
    }

    // Helper function to process tickets
    const processTickets = (tickets: any[], isIncident: boolean) => {
      tickets.forEach(ticket => {
        const dateStr = ticket.opened || ticket.created || ticket.opened_at || ticket.Opened;
        if (!dateStr) return;

        let ticketDate: Date;
        try {
          // Handle different date formats
          if (typeof dateStr === 'string') {
            if (dateStr.includes('/') && (dateStr.includes('AM') || dateStr.includes('PM'))) {
              ticketDate = new Date(dateStr);
            } else {
              ticketDate = parseISO(dateStr);
            }
          } else {
            ticketDate = new Date(dateStr);
          }
          
          if (!isValid(ticketDate)) {
            ticketDate = new Date(dateStr);
          }
        } catch {
          return;
        }

        if (isValid(ticketDate) && isSameDay(ticketDate, selectedDate)) {
          const hour = getHours(ticketDate);
          const hourKey = `${hour.toString().padStart(2, '0')}:00`;
          
          // Update counts
          if (isIncident) {
            hourlyBuckets[hour].incidents++;
          } else {
            hourlyBuckets[hour].catalogTasks++;
          }
          hourlyBuckets[hour].total++;

          // Add ticket details
          const ticketData = {
            number: ticket['number'] || ticket['Number'] || '',
            description: ticket['short_description'] || ticket['description'] || ticket['Short description'] || '',
            openedTime: format(ticketDate, 'HH:mm:ss'),
            type: (isIncident ? 'incident' : 'task') as 'incident' | 'task',
            priority: ticket['priority'] || ticket['Priority'] || undefined,
            sortTime: ticketDate
          };
          ticketsTracker[hourKey].push(ticketData);
        }
      });
    };

    // Process incidents and catalog tasks
    processTickets(incidents, true);
    processTickets(catalogTasks, false);

    // Sort tickets in each hour by time (ascending order)
    Object.keys(ticketsTracker).forEach(hour => {
      ticketsTracker[hour].sort((a, b) => a.sortTime.getTime() - b.sortTime.getTime());
    });

    return { hourlyData: hourlyBuckets, ticketsByHour: ticketsTracker };
  }, [incidents, catalogTasks, selectedDate]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    
    const data = hourlyData.find(bucket => bucket.hour === label);
    if (!data) return null;

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-800">{`Time: ${label}`}</p>
        <p className="text-blue-600">{`Incidents: ${data.incidents}`}</p>
        <p className="text-green-600">{`Catalog Tasks: ${data.catalogTasks}`}</p>
        <p className="font-semibold text-gray-800">{`Total: ${data.total}`}</p>
      </div>
    );
  };

  const totalTickets = hourlyData.reduce((sum, bucket) => sum + bucket.total, 0);

  // Handle bar click
  const handleBarClick = (data: any) => {
    if (data && data.activeLabel) {
      const clickedHour = data.activeLabel;
      setSelectedHour(clickedHour);
      setIsDetailsOpen(true);
    }
  };

  // Get tickets for selected hour
  const selectedHourTickets = selectedHour ? ticketsByHour[selectedHour] || [] : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="text-sm font-normal text-gray-500">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')} • {totalTickets} tickets
          </span>
        </CardTitle>
        <p className="text-sm text-muted-foreground">Click on any bar to see detailed ticket list for that hour</p>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={hourlyData} 
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              onClick={handleBarClick}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="hour"
                tick={{ fontSize: 12 }}
                interval={1}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                label={{ value: 'Ticket Count', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="incidents" 
                fill="#ef4444" 
                name="Incidents"
                radius={[2, 2, 0, 0]}
              />
              <Bar 
                dataKey="catalogTasks" 
                fill="#3b82f6" 
                name="Catalog Tasks"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Ticket Details Section */}
        {selectedHour && (
          <div className="mt-6">
            <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium">
                    {selectedHour} Tickets ({selectedHourTickets.length})
                  </span>
                </div>
                {isDetailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3">
                {selectedHourTickets.length > 0 ? (
                  <div className="space-y-2">
                    {selectedHourTickets.map((ticket, index) => (
                      <div key={index} className="p-3 border rounded-lg bg-background">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge 
                                variant={ticket.type === 'incident' ? 'destructive' : 'default'}
                                className={ticket.type === 'incident' ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600 text-white'}
                              >
                                {ticket.type === 'incident' ? 'INC' : 'TASK'}
                              </Badge>
                              <span className="font-mono text-sm font-medium">{ticket.number}</span>
                              {ticket.priority && (
                                <Badge variant="outline" className="text-xs">
                                  P{ticket.priority}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-1">{ticket.description}</p>
                            <p className="text-xs text-muted-foreground">Opened: {ticket.openedTime}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    No tickets found for {selectedHour}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
        
        {totalTickets === 0 && (
          <div className="text-center py-8 text-gray-500">
            No tickets found for {format(selectedDate, 'MMMM d, yyyy')}
          </div>
        )}
      </CardContent>
    </Card>
  );
} 